import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Copy,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RotateCcw,
  ArrowLeft,
  Loader2,
  Link as LinkIcon,
} from "lucide-react";
import ReceiptCard from "@/components/ReceiptCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOTAL_SECONDS = 5 * 60;

function formatBRL(v) {
  return Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatTimer(s) {
  const m = Math.floor(s / 60).toString().padStart(1, "0");
  const r = (s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

function statusMeta(status) {
  switch (status) {
    case "COMPLETO":
      return {
        label: "Pagamento confirmado",
        color: "var(--neon)",
        Icon: CheckCircle2,
        ring: "rgba(0, 255, 136, 0.55)",
      };
    case "FALHA":
    case "CANCELADO":
      return {
        label: "Pagamento falhou",
        color: "#ff5577",
        Icon: AlertTriangle,
        ring: "rgba(255, 85, 119, 0.55)",
      };
    default:
      return {
        label: "Aguardando pagamento...",
        color: "var(--neon)",
        Icon: Clock,
        ring: "rgba(0, 255, 136, 0.45)",
      };
  }
}

export default function PixPanel({ payment, onReset, hideBack = false }) {
  const [status, setStatus] = useState(payment.status || "PENDENTE");
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const isPaid = status === "COMPLETO";
  const isExpired = secondsLeft <= 0 && !isPaid;
  const isFailed = status === "FALHA" || status === "CANCELADO";
  const isFinal = isPaid || isFailed;
  const meta = statusMeta(isExpired && !isFinal ? "FALHA" : status);

  const paymentLink = useMemo(() => {
    if (typeof window === "undefined" || !payment?.transaction_id) return "";
    return `${window.location.origin}/pay/${payment.transaction_id}`;
  }, [payment]);

  const qrSrc = useMemo(() => {
    if (!payment?.qr_code_base64) return null;
    return payment.qr_code_base64.startsWith("data:")
      ? payment.qr_code_base64
      : `data:image/png;base64,${payment.qr_code_base64}`;
  }, [payment]);

  // Countdown
  useEffect(() => {
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  // Polling status
  useEffect(() => {
    if (isFinal || isExpired) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${API}/payments/${payment.transaction_id}/status`
        );
        if (data?.status && data.status !== status) {
          setStatus(data.status);
          if (data.status === "COMPLETO") toast.success("Pagamento confirmado!");
          if (data.status === "FALHA") toast.error("Pagamento falhou");
        }
      } catch (e) {
        // silent retry
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [payment.transaction_id, status, isFinal, isExpired]);

  const fallbackCopy = (text) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  };

  const selectCodeInPanel = () => {
    const el = document.querySelector('[data-testid="pix-copypaste"]');
    if (!el) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const handleCopy = async () => {
    const text = payment?.copy_paste;
    if (!text) return;

    let success = false;

    // Try modern Clipboard API (requires secure context + permission)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        success = true;
      } catch (_) {
        success = false;
      }
    }

    // Fallback to execCommand
    if (!success) {
      success = fallbackCopy(text);
    }

    if (success) {
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Last resort: select the text so the user can copy manually
      selectCodeInPanel();
      toast.message("Selecione o código e use Copiar do navegador", {
        description: "Seu navegador bloqueou a cópia automática.",
      });
    }
  };

  const handleCopyLink = async () => {
    if (!paymentLink) return;
    let success = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(paymentLink);
        success = true;
      } catch (_) {}
    }
    if (!success) success = fallbackCopy(paymentLink);
    if (success) {
      setLinkCopied(true);
      toast.success("Link de pagamento copiado!");
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      toast.error("Não foi possível copiar o link");
    }
  };

  const handleShareLinkWhatsApp = () => {
    if (!paymentLink) return;
    const text = encodeURIComponent(
      `Pague seu pedido via PIX na MÍDIA STORE 💚\n\nValor: R$ ${Number(payment.amount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}\n\n${paymentLink}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="neon-card rounded-[28px] p-6 sm:p-8 rise" data-testid="pix-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        {hideBack ? (
          <span className="text-[10px] mono uppercase tracking-[0.22em] text-[var(--muted)]">
            link de pagamento
          </span>
        ) : (
          <button
            type="button"
            onClick={onReset}
            className="text-xs mono uppercase tracking-[0.18em] text-[var(--muted)] hover:text-white inline-flex items-center gap-1.5 transition"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> voltar
          </button>
        )}
        <span className="text-[10px] mono uppercase tracking-[0.22em] text-[var(--muted)]">
          ID · {payment.transaction_id?.slice(-8)}
        </span>
      </div>

      <h2 className="text-2xl sm:text-3xl font-bold text-center neon-text rise rise-1" data-testid="pix-title">
        Escaneie o QR Code
      </h2>

      {/* Status */}
      <div className="mt-3 flex items-center justify-center gap-2.5 rise rise-2" data-testid="pix-status">
        <span
          className="relative flex w-2.5 h-2.5"
          style={{ filter: `drop-shadow(0 0 6px ${meta.ring})` }}
        >
          {!isFinal && !isExpired && (
            <span
              className="absolute inline-flex h-full w-full rounded-full opacity-60 pulse-dot"
              style={{ background: meta.color }}
            />
          )}
          <span
            className="relative inline-flex rounded-full h-2.5 w-2.5"
            style={{ background: meta.color }}
          />
        </span>
        <span
          className="text-sm mono uppercase tracking-[0.18em]"
          style={{ color: meta.color }}
        >
          {isExpired && !isFinal ? "Tempo expirado" : meta.label}
        </span>
        {!isFinal && !isExpired && (
          <span className="text-sm mono text-white tabular-nums" data-testid="pix-countdown">
            · {formatTimer(secondsLeft)}
          </span>
        )}
      </div>

      {/* Amount */}
      <div className="text-center mt-7 rise rise-2">
        <p className="text-[11px] mono uppercase tracking-[0.22em] text-[var(--muted)]">
          Valor
        </p>
        <p
          className="mt-1 mono text-5xl sm:text-6xl font-extrabold tracking-tight text-white tabular-nums"
          data-testid="pix-amount"
        >
          <span className="text-[var(--neon)] text-3xl sm:text-4xl align-top mr-2">R$</span>
          {formatBRL(payment.amount)}
        </p>
      </div>

      {/* QR */}
      <div className="flex justify-center mt-7 rise rise-3">
        {isPaid ? (
          <PaidStamp />
        ) : qrSrc ? (
          <div className="qr-frame">
            <span className="corner tl" />
            <span className="corner tr" />
            <span className="corner bl" />
            <span className="corner br" />
            <img
              src={qrSrc}
              alt="QR Code PIX"
              className="w-56 h-56 sm:w-64 sm:h-64 block"
              data-testid="pix-qrcode"
            />
          </div>
        ) : (
          <div className="w-56 h-56 sm:w-64 sm:h-64 flex items-center justify-center text-[var(--muted)] mono text-xs">
            QR indisponível
          </div>
        )}
      </div>

      {/* Copy paste — only while waiting for payment */}
      {!isPaid && (
        <div className="mt-7 rise rise-4" data-testid="pix-copypaste-wrapper">
        <p className="text-[11px] mono uppercase tracking-[0.22em] text-[var(--muted)] mb-2">
          Código PIX (copia e cola)
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div
            className="flex-1 neon-input rounded-xl px-4 py-3 mono text-xs sm:text-sm break-all max-h-[88px] overflow-auto cursor-pointer"
            data-testid="pix-copypaste"
            onClick={handleCopy}
            title="Toque para copiar"
          >
            {payment.copy_paste || "—"}
          </div>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!payment.copy_paste}
            className="neon-btn rounded-xl px-5 py-3 mono uppercase tracking-[0.18em] text-xs flex items-center justify-center gap-2 sm:min-w-[140px]"
            data-testid="copy-pix-btn"
          >
            {copied ? (
              <>
                <CheckCircle2 className="w-4 h-4" /> Copiado
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" /> Copiar
              </>
            )}
          </button>
        </div>
        </div>
      )}

      {/* Payment link sharing — only while waiting for payment, not on /pay/:id */}
      {!isPaid && !hideBack && paymentLink && (
        <div className="mt-5 rise rise-4" data-testid="payment-link-block">
          <p className="text-[11px] mono uppercase tracking-[0.22em] text-[var(--muted)] mb-2">
            Link de pagamento (envie ao cliente)
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div
              className="flex-1 neon-input rounded-xl px-4 py-3 mono text-xs sm:text-sm break-all cursor-pointer"
              onClick={handleCopyLink}
              data-testid="payment-link"
              title="Toque para copiar"
            >
              {paymentLink}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopyLink}
                className="neon-ghost-btn rounded-xl px-4 py-3 mono uppercase tracking-[0.16em] text-[11px] flex items-center justify-center gap-1.5 flex-1 sm:flex-none"
                data-testid="copy-link-btn"
              >
                {linkCopied ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Copiado
                  </>
                ) : (
                  <>
                    <LinkIcon className="w-3.5 h-3.5" /> Copiar
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleShareLinkWhatsApp}
                className="neon-ghost-btn rounded-xl px-4 py-3 mono uppercase tracking-[0.16em] text-[11px] flex items-center justify-center gap-1.5 flex-1 sm:flex-none"
                data-testid="share-link-btn"
                style={{ borderColor: "rgba(0,255,136,0.45)", color: "var(--neon)" }}
              >
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt + WhatsApp share — only when paid */}
      {isPaid && <ReceiptCard payment={payment} />}

      {/* Bottom action */}
      <div className="mt-6 flex items-center justify-between text-xs mono text-[var(--muted)] tracking-wide">
        <span className="flex items-center gap-1.5">
          {isFinal || isExpired ? (
            <RotateCcw className="w-3.5 h-3.5 text-[var(--neon)]" />
          ) : (
            <Loader2 className="w-3.5 h-3.5 spin text-[var(--neon)]" />
          )}
          {isFinal || isExpired ? "transação encerrada" : "verificando pagamento..."}
        </span>
        <button
          type="button"
          onClick={onReset}
          className="neon-ghost-btn rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-[0.18em]"
          data-testid="new-payment-btn"
        >
          novo pagamento
        </button>
      </div>
    </section>
  );
}

function PaidStamp() {
  return (
    <div
      className="w-56 h-56 sm:w-64 sm:h-64 rounded-3xl flex flex-col items-center justify-center pop"
      style={{
        background:
          "radial-gradient(circle at 50% 40%, rgba(0,255,136,0.25), rgba(0,255,136,0.05) 60%, transparent 75%)",
        border: "1px solid rgba(0,255,136,0.4)",
        boxShadow: "0 0 60px rgba(0,255,136,0.35)",
      }}
      data-testid="pix-paid-stamp"
    >
      <CheckCircle2 className="w-20 h-20 text-[var(--neon)]" strokeWidth={1.4} />
      <p className="mt-3 mono uppercase tracking-[0.22em] text-[var(--neon)] text-sm">
        Pagamento OK
      </p>
    </div>
  );
}
